import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from './translations';

export type Language = 'en' | 'es' | 'hi';

const SUPPORTED_LANGUAGES: Language[] = ['en', 'es', 'hi'];
const STORAGE_KEY = 'assetfactory-language';

function detectLanguage(): Language {
  const saved = window.localStorage.getItem(STORAGE_KEY) as Language | null;
  if (saved && SUPPORTED_LANGUAGES.includes(saved)) return saved;

  const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const browserLanguage of browserLanguages) {
    const language = browserLanguage.toLowerCase().split('-')[0] as Language;
    if (SUPPORTED_LANGUAGES.includes(language)) return language;
  }

  return 'en';
}

interface I18nContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  content: typeof translations.en;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(detectLanguage);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    content: translations[language] as typeof translations.en,
  }), [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used within LanguageProvider');
  return context;
}
